import React from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';

export default function CartDrawer() {
  const { items, isOpen, setIsOpen, removeItem, updateQuantity, totalPrice } = useCart();
  const { t, language } = useLanguage();

  if (!isOpen) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('sr-RS').format(price);
  };

  const getProductName = (product) => {
    return language === 'sr' ? product.name_sr : product.name_en;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40"
        onClick={() => setIsOpen(false)}
        data-testid="cart-backdrop"
      />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-md bg-[#0A0A0A] border-l border-white/5 z-50 animate-slide-in-right"
        data-testid="cart-drawer"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/5">
            <h2 className="font-heading text-2xl text-white">{t('cart_title')}</h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-white/50 hover:text-white transition-colors"
              data-testid="cart-close"
            >
              <X size={24} strokeWidth={1.5} />
            </button>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto p-6">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <ShoppingBag size={48} className="text-white/20 mb-4" strokeWidth={1} />
                <p className="text-white/40 mb-6">{t('cart_empty')}</p>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gold hover:text-gold-light transition-colors text-sm tracking-wider uppercase"
                >
                  {t('cart_continue')}
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {items.map((item) => (
                  <div
                    key={item.product.id}
                    className="flex gap-4"
                    data-testid={`cart-item-${item.product.id}`}
                  >
                    <div className="w-20 h-24 bg-surface flex-shrink-0 flex items-center justify-center p-2">
                      <img
                        src={item.product.image_url}
                        alt={getProductName(item.product)}
                        className="max-h-full w-auto"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-white text-lg truncate">
                        {getProductName(item.product)}
                      </h3>
                      <p className="text-gold text-sm mt-1">
                        {formatPrice(item.product.price)} RSD
                      </p>
                      <div className="flex items-center gap-3 mt-3">
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center border border-white/10 hover:border-gold/50 transition-colors"
                          data-testid={`cart-item-decrease-${item.product.id}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-white w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center border border-white/10 hover:border-gold/50 transition-colors"
                          data-testid={`cart-item-increase-${item.product.id}`}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(item.product.id)}
                      className="text-white/30 hover:text-red-500 transition-colors self-start"
                      data-testid={`cart-item-remove-${item.product.id}`}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="p-6 border-t border-white/5">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white/50">{t('cart_total')}</span>
                <span className="font-heading text-2xl text-white">
                  {formatPrice(totalPrice)} <span className="text-sm text-white/50">RSD</span>
                </span>
              </div>
              <p className="text-white/30 text-xs text-right mb-6">
                {language === 'sr' ? '+ dostava' : '+ shipping'}
              </p>
              <Link
                to="/checkout"
                onClick={() => setIsOpen(false)}
                className="block w-full bg-gold hover:bg-gold-light text-black text-center py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300"
                data-testid="cart-checkout-button"
              >
                {t('cart_checkout')}
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
